/**
 * ⭐ A button that is actually a LINK — same treatment, real anchor semantics.
 *
 * ## Why this has to exist, and why it is not a variant of `Button`
 *
 * **Most of the page-level primary actions in this console are navigations**, not commands: New app,
 * New tenant, New data plane, Rollout, page-level Promote, Pod console. Each has a URL. Rendering
 * those as `<button onClick={() => navigate(…)}>` throws away everything an anchor gives the
 * operator for free:
 *
 *   · **right-click → open in new tab** — an operator comparing two data planes does this constantly;
 *   · **middle-click**, same;
 *   · **copy link address** — how a URL gets pasted into an incident channel;
 *   · the browser's own status-bar preview of where the control goes;
 *   · `role="link"`, so a screen reader announces *"link"* and the user knows the page will change.
 *
 * ⛔ And the alternative — duplicating the class strings at the call site — is the exact defect the
 * component exists to remove. Without this file the sweep would have delivered a minority of the
 * recommended primaries, and the ones it dropped would have been the navigational ones.
 *
 * ## Shape: a sibling, not a polymorphic `as`
 *
 * A polymorphic `as` prop would need a discriminated props union (`to` is required for a link and
 * meaningless on a button; `disabled` and `type` are the reverse), and in practice that lands on a
 * cast to keep TypeScript quiet — which this repo bans outright. Two small components with honest,
 * non-overlapping prop types are clearer to read and impossible to misuse.
 *
 * ⭐ **The visual treatment still has exactly ONE definition:** both call `buttonClass()`, and
 * `button-link-parity.test.ts` asserts each renders a byte-identical class string for every
 * tone x size pair. "One definition of the parts" is not the same property as "one definition of the
 * result" — `${BASE} ${TONE} ${SIZE}` assembled in two places can drift in order or spacing, or one
 * side can quietly gain a fourth term.
 *
 * ## ⛔ THERE IS DELIBERATELY NO `disabled`
 *
 * HTML has no disabled anchor. The two things people reach for both fail:
 *   · `aria-disabled="true"` on an `<a href>` announces "disabled" and **still navigates on click** —
 *     the worst outcome, because the control lies to assistive tech and works anyway;
 *   · removing `href` makes it un-focusable and un-announced, i.e. it vanishes for keyboard users.
 *
 * ⇒ **If a navigation is unavailable, do not render this.** Per the house hide-vs-disable rule:
 * a *permission* gap ⇒ HIDE the control; a *state* gap ⇒ render a real `<Button disabled>` with the
 * reason stated next to it, because a disabled control with no reason is indistinguishable from a
 * broken one. That is a genuinely different control, and it should be, since it does something
 * different: it explains itself instead of navigating.
 *
 * ⚠️ Consequence of sharing the tone classes: their `disabled:` utilities are inert on an anchor
 * (the pseudo-class never matches). Harmless, and it keeps the strings identical — which is the
 * property being protected. The `aria-disabled:` utilities match only that attribute, and no link
 * here carries it.
 */
import { Link, type LinkProps } from 'react-router';
import { buttonClass, type ButtonSize, type ButtonTone } from './Button';
import type { ReactNode, Ref } from 'react';

export interface ButtonLinkProps extends Omit<LinkProps, 'className' | 'children'> {
  readonly tone?: ButtonTone;
  readonly size?: ButtonSize;
  readonly children: ReactNode;
  /** Focus restoration, same reason as `Button`'s — React 19 forwards it as an ordinary prop. */
  readonly ref?: Ref<HTMLAnchorElement>;
}

export function ButtonLink({
  tone = 'default',
  size = 'sm',
  children,
  ...rest
}: ButtonLinkProps): React.JSX.Element {
  return (
    // ⛔ `className` is not forwardable here either (it is `Omit`ted above) — a call site that could
    // append its own utilities is how one definition decays back into many.
    <Link {...rest} className={buttonClass({ tone, size })}>
      {children}
    </Link>
  );
}
